// ---------------------------------------------------------------------------
// Business Time — Weekend Exclusion Utility
// Pure function to subtract weekend time from elapsed duration.
// ---------------------------------------------------------------------------

const MS_PER_DAY = 86_400_000;

/**
 * Subtract full and partial weekend-day time from the elapsed interval [startMs, endMs].
 *
 * Iterates calendar days (in the given timezone) between start and end.
 * For each day whose `getDay()` is in `weekendDays`, subtracts the overlap
 * between that calendar day and the [start, end] interval.
 *
 * @param startMs  - interval start (epoch ms)
 * @param endMs    - interval end (epoch ms)
 * @param weekendDays - days to exclude (JS getDay(): 0=Sun … 6=Sat)
 * @param tz       - IANA timezone string (e.g. "Europe/Kyiv")
 * @returns adjusted elapsed ms (never negative)
 */
export function subtractWeekendMs(
  startMs: number,
  endMs: number,
  weekendDays: number[],
  tz: string,
): number {
  if (endMs <= startMs || weekendDays.length === 0) {
    return Math.max(0, endMs - startMs);
  }

  const weekendSet = new Set(weekendDays);
  const elapsed = endMs - startMs;
  let weekendMs = 0;

  // Find the start of the calendar day (in tz) that contains startMs.
  // We iterate day-by-day from there.
  let cursor = startOfDayInTz(startMs, tz);

  while (cursor < endMs) {
    const nextDay = cursor + MS_PER_DAY;
    const dayOfWeek = getDayOfWeekInTz(cursor, tz);

    if (weekendSet.has(dayOfWeek)) {
      // Overlap of [cursor, nextDay) with [startMs, endMs)
      const overlapStart = Math.max(cursor, startMs);
      const overlapEnd = Math.min(nextDay, endMs);
      if (overlapEnd > overlapStart) {
        weekendMs += overlapEnd - overlapStart;
      }
    }

    cursor = nextDay;
  }

  return Math.max(0, elapsed - weekendMs);
}

/**
 * Get the epoch ms of the start of the calendar day (midnight) in `tz`
 * that contains the given timestamp.
 */
function startOfDayInTz(epochMs: number, tz: string): number {
  // Format the date parts in the target timezone
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date(epochMs));

  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? "0";

  const year = Number(get("year"));
  const month = Number(get("month"));
  const day = Number(get("day"));
  const hour = Number(get("hour") === "24" ? "0" : get("hour"));
  const minute = Number(get("minute"));
  const second = Number(get("second"));

  // The offset between the wall-clock time in tz and UTC:
  // epochMs = wall-clock-as-UTC + offset  ⟹  offset = epochMs - wall-clock-as-UTC
  const wallAsUtcMs = Date.UTC(year, month - 1, day, hour, minute, second);
  const offsetMs = epochMs - wallAsUtcMs;

  // Midnight in tz for this day
  const midnightWallUtcMs = Date.UTC(year, month - 1, day, 0, 0, 0);
  return midnightWallUtcMs + offsetMs;
}

/**
 * Get the day-of-week (0=Sun … 6=Sat) for a timestamp in the given timezone.
 */
function getDayOfWeekInTz(epochMs: number, tz: string): number {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
  });
  const weekday = formatter.format(new Date(epochMs));
  const map: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return map[weekday] ?? 0;
}
